@if (!form()) {
  <cor-skeleton [type]="SKELETON_TYPES.DETAILS" />
} @else {
  <div class="tw:flex tw:flex-col tw:gap-2">
    @for (riskCategory of riskCategories(); track riskCategory.key) {
      <p-panel
        [style]="{ '--p-panel-content-padding': 0 }"
        [toggleable]="true"
        iconPos="start"
        toggler="header"
        [collapsed]="true">
        <!-- Start: header -->
        <ng-template #header>
          <div
            class="tw:flex-auto tw:flex tw:items-center tw:justify-between tw:ps-3 tw:font-medium">
            <div
              class="tw:flex tw:gap-2 tw:items-center"
              [attr.test-id]="'risk-category-header-' + riskCategory.name">
              <i
                class="pi"
                [ngClass]="riskCategory.icon"
                [style]="{
                  color: riskCategory.color,
                }"></i>
              <div style="font-size: 1.2rem">
                {{ riskCategory.name }}
              </div>
            </div>
            <div class="tw:flex tw:gap-2">
              @for (risk of riskCategory.risks; track risk.key) {
                @let riskForm =
                  form().controls.findings.controls[riskCategory.key].controls[risk.key];
                <p-tag
                  (click)="onRiskSelectionChange(risk, riskCategory.key)"
                  [pTooltip]="
                    riskForm.controls.riskMitigationMethod.value?.length > 0
                      ? riskForm.invalid
                        ? risk.name + ' - form errors'
                        : risk.name
                      : risk.name + ' - not answered'
                  "
                  class="tw:gap-0 tw:p-2"
                  [icon]="
                    riskForm.controls.riskMitigationMethod.value?.length > 0 && !riskForm.invalid
                      ? 'pi pi-check'
                      : 'pi pi-exclamation-triangle'
                  "
                  [severity]="
                    riskForm.controls.riskMitigationMethod.value?.length > 0
                      ? riskForm.invalid
                        ? 'danger'
                        : 'success'
                      : 'warn'
                  " />
              }

              <p-tag
                class="tw:px-3 tw:py-1"
                severity="secondary"
                [value]="`${riskCategory.risks.length} Risks`" />
            </div>
          </div>
        </ng-template>
        <!-- End: header -->

        <!-- Start: header icons -->
        <ng-template
          #headericons
          let-collapsed>
          <i
            class="pi"
            [ngClass]="{
              'pi-chevron-down': !collapsed,
              'pi-chevron-right': collapsed,
            }"></i>
        </ng-template>
        <!-- End: header icons -->

        <!-- Start: content -->
        <div class="tw:grid tw:grid-cols-4">
          <!-- Sidepanel -->
          <div class="tw:flex tw:flex-col">
            <label class="tw:px-3 tw:pt-1">Risks</label>
            <div class="tw:flex tw:gap-1 tw:flex-col tw:p-2">
              @for (risk of riskCategory.risks; track risk.name) {
                @let riskForm =
                  form().controls.findings.controls[riskCategory.key].controls[risk.key];
                <a
                  pRipple
                  class="tw:flex tw:items-center tw:gap-2 tw:p-2 tw:rounded tw:cursor-pointer"
                  [ngClass]="{
                    'tw:bg-primary/15 tw:text-primary':
                      selectedRisk()[riskCategory.key]?.name === risk.name,
                  }"
                  (click)="onRiskSelectionChange(risk, riskCategory.key)"
                  [attr.test-id]="'risk-' + risk.name">
                  <p-tag
                    class="tw:gap-0 tw:p-2"
                    [pTooltip]="
                      riskForm.controls.riskMitigationMethod.value?.length > 0
                        ? riskForm.invalid && 'Form errors'
                        : ' Not answered'
                    "
                    [icon]="
                      riskForm.controls.riskMitigationMethod.value?.length > 0 && !riskForm.invalid
                        ? 'pi pi-check'
                        : 'pi pi-exclamation-triangle'
                    "
                    [severity]="
                      riskForm.controls.riskMitigationMethod.value?.length > 0
                        ? riskForm.invalid
                          ? 'danger'
                          : 'success'
                        : 'warn'
                    " />
                  <span
                    class="tw:ml-2 tw:font-semibold"
                    style="color: inherit">
                    {{ risk.name }}
                  </span>
                </a>
              }
            </div>
          </div>
          <!-- Selcted Risk Details -->
          <div
            class="tw:border-l tw:border-surface tw:col-span-3"
            test-id="risk-details">
            @let currentRisk = selectedRisk()[riskCategory.key];
            @let riskForm =
              form().controls.findings.controls[riskCategory.key].controls[currentRisk.key];
            <div class="tw:flex tw:flex-col tw:px-3 tw:py-2 tw:border-b tw:border-surface">
              <div class="tw:text-color tw:font-semibold">
                {{ currentRisk.name }}
              </div>
              <small class="tw:text-muted-color">
                {{ currentRisk.description }}
              </small>
            </div>
            <div class="tw:flex tw:flex-col tw:gap-2 tw:p-3">
              <div class="tw:text-color tw:font-semibold">How was this risk mitigated?</div>
              <div class="tw:flex tw:flex-col">
                @if (
                  riskForm.controls.riskMitigationMethod.value.length === 0 &&
                  registryService.isEditable()
                ) {
                  <!-- Initial Risk mitigation selection -->
                  <div class="tw:grid tw:grid-cols-3 tw:gap-3">
                    @for (
                      riskMitigationMethod of riskMitigationMethods();
                      track riskMitigationMethod.id
                    ) {
                      <div
                        test-id="risk-assessment-details-mark-as-dirty"
                        class="tw:flex tw:flex-col tw:items-stretch tw:rounded tw:border tw:border-surface tw:hover:bg-primary/15 tw:hover:border-primary tw:px-3 tw:py-2 tw:cursor-pointer"
                        (click)="
                          riskForm.controls.riskMitigationMethod.patchValue([
                            riskMitigationMethod.id,
                          ]);
                          form().markAsDirty()
                        ">
                        <label class="tw:cursor-pointer">{{ riskMitigationMethod.name }}</label>
                        <small class="tw:text-muted-color">
                          {{ riskMitigationMethod.description }}
                        </small>
                      </div>
                    }
                  </div>
                } @else {
                  <!-- Start: Risk Mitigation method -->
                  @if (registryService.isEditable()) {
                    <p-multiselect
                      test-id="risk-assessment-details-risk-mitigation-method"
                      class="tw:mb-3"
                      appendTo="body"
                      [fluid]="true"
                      display="chip"
                      [maxSelectedLabels]="100"
                      [formControl]="riskForm.controls.riskMitigationMethod"
                      placeholder="Select the risk mitigation method used"
                      [options]="riskMitigationMethods()"
                      optionLabel="name"
                      optionValue="id">
                      <ng-template
                        let-item
                        pTemplate="item">
                        <div
                          class="tw:flex tw:items-end tw:gap-2 tw:w-full"
                          [attr.test-id]="'risk-mitigation-method-' + item.name">
                          <div>{{ item.name }}</div>
                          <small
                            class="tw:text-muted-color tw:truncate"
                            [title]="item.description">
                            {{ item.description || '' }}
                          </small>
                        </div>
                      </ng-template>
                    </p-multiselect>
                  } @else {
                    <div class="tw:flex tw:flex-wrap tw:gap-1 tw:mb-3">
                      @if (
                        form().controls.findings.controls[riskCategory.key].controls[
                          currentRisk.key
                        ].controls.riskMitigationMethod.value.length > 0
                      ) {
                        @for (
                          riskMitigationMethod of form().controls.findings.controls[
                            riskCategory.key
                          ].controls[currentRisk.key].controls.riskMitigationMethod.value;
                          track riskMitigationMethod
                        ) {
                          <p-chip
                            class="tw:rounded-sm tw:py-1 tw:font-semibold tw:text-color"
                            [attr.test-id]="
                              'risk-mitigation-method-' +
                              RISK_MITIGATION_DETAILS[riskMitigationMethod].name
                            "
                            [label]="RISK_MITIGATION_DETAILS[riskMitigationMethod].name"
                            [pTooltip]="
                              riskMitigationMethod === RISK_MITIGATION_METHOD.OTHER
                                ? riskForm.controls.otherValue.value
                                : RISK_MITIGATION_DETAILS[riskMitigationMethod].description
                            " />
                        }
                      } @else {
                        <span class="tw:text-muted-color tw:italic">
                          Risk Evaluation not filled in yet. Click Edit to fill in.
                        </span>
                      }
                    </div>
                  }
                  <!-- End: Risk Mitigation method -->

                  <!-- Start: Other vale -->
                  @if (
                    riskForm.controls.riskMitigationMethod.value
                      | includes: RISK_MITIGATION_METHOD.OTHER
                  ) {
                    <cor-input-container
                      class="col-12"
                      label="Other Value"
                      test-id="value">
                      <input
                        test-id="risk-assessment-details-other-value"
                        pInputText
                        type="text"
                        [fluid]="true"
                        [formControl]="riskForm.controls.otherValue"
                        placeholder="As you've selected 'Other', please explain how the risk was mitigated" />
                    </cor-input-container>
                  }
                  <!-- End: Other vale -->

                  <!-- Start:Comment -->
                  @if (riskForm.controls.explanation.value || registryService.isEditable()) {
                    <cor-input-container
                      class="col-12"
                      label="Comment"
                      test-id="risk-comment">
                      <cor-description
                        test-id="risk-assessment-details-explanation"
                        [minHeight]="100"
                        placeholder="Additional comments explaining in more detail what approaches were used to manage the risk"
                        [formControl]="riskForm.controls.explanation" />
                    </cor-input-container>
                  }
                  <!-- End:Comment -->
                }
              </div>
            </div>
          </div>
        </div>
        <!-- End: content -->
      </p-panel>
    }
  </div>
}
